/**
 * AgroTech — Treatment Efficacy  (Step 8.6 of the 9-step intelligence loop)
 *
 * For every completed remediation mission, shows:
 *   • Pre-treatment vs. post-treatment NDVI mean (fetched from satellite imagery records)
 *   • Recovery delta and AI-derived efficacy score
 *   • Re-survey status (scheduled / complete / overdue)
 *   • Per-field efficacy table with status badges
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, Repeat2, BarChart2, CheckCircle2,
  RefreshCw, AlertTriangle, Clock, Activity,
  ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import { satelliteImageryApi, satelliteBoundariesApi } from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RemediationMission {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    remediation_type?: string;
    target_zone_ha?: number;
    recommended_drone?: string;
    survey_mission_id?: string;
    anomaly_id?: string;
    boundary_id?: string;
    step7_confirmation?: string;
    treatment_date?: string;
    reverify_after_days?: number;
    [key: string]: any;
  };
}

interface ImageryRecord {
  id: string;
  acquisition_date: string;
  satellite: string;
  cloud_cover_percentage: number;
  indices: { index_type: string; mean_value: number }[];
}

interface EfficacyRecord {
  mission: RemediationMission;
  preTreatmentNdvi: number | null;
  postTreatmentNdvi: number | null;
  recoveryDelta: number | null;
  efficacyScore: number | null;   // 0–100
  reSurveyStatus: 'scheduled' | 'complete' | 'overdue' | 'no_data';
  reSurveyDue: string | null;
  boundaryName: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Derive an efficacy score (0–100) from NDVI recovery delta vs. initial drop */
function calcEfficacyScore(pre: number | null, post: number | null): number | null {
  if (pre === null || post === null) return null;
  const delta = post - pre;
  // Treat a recovery of ≥0.05 NDVI as 100 %, scale linearly between -0.10 and +0.05
  const clamped = Math.max(-0.10, Math.min(0.05, delta));
  return Math.round(((clamped + 0.10) / 0.15) * 100);
}

function getNdviMean(records: ImageryRecord[], date: string, windowDays = 20): number | null {
  const target = new Date(date).getTime();
  const half = windowDays * 86400000;
  const near = records.filter((r) => {
    const t = new Date(r.acquisition_date).getTime();
    return Math.abs(t - target) <= half;
  });
  if (near.length === 0) return null;
  const vals: number[] = [];
  near.forEach((r) => {
    const idx = r.indices.find((i) => i.index_type === 'NDVI');
    if (idx) vals.push(Number(idx.mean_value));
  });
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

function deriveReSurveyDue(mission: RemediationMission): string | null {
  const base = mission.metadata?.treatment_date ?? mission.updated_at;
  if (!base) return null;
  const days = mission.metadata?.reverify_after_days ?? 14;
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function deriveReSurveyStatus(
  due: string | null,
  postNdvi: number | null,
): EfficacyRecord['reSurveyStatus'] {
  if (!due) return 'no_data';
  if (postNdvi !== null) return 'complete';
  const today = new Date().toISOString().slice(0, 10);
  return due < today ? 'overdue' : 'scheduled';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const RESUR_STYLE: Record<EfficacyRecord['reSurveyStatus'], string> = {
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  complete:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  overdue:   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  no_data:   'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};
const RESUR_LABEL: Record<EfficacyRecord['reSurveyStatus'], string> = {
  scheduled: 'Re-survey Scheduled',
  complete:  'Re-survey Complete',
  overdue:   'Re-survey Overdue',
  no_data:   'No data',
};

function EfficacyBar({ score }: { score: number | null }) {
  if (score === null) return <span className="text-xs text-gray-400">—</span>;
  const colour =
    score >= 70 ? 'bg-emerald-500' :
    score >= 40 ? 'bg-amber-400' :
    'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-8 text-right">{score}%</span>
    </div>
  );
}

function NdviDelta({ pre, post }: { pre: number | null; post: number | null }) {
  if (pre === null || post === null) return <span className="text-xs text-gray-400">—</span>;
  const delta = post - pre;
  const sign = delta >= 0 ? '+' : '';
  const colour = delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  return (
    <span className={`text-sm font-mono font-bold ${colour}`}>
      {sign}{delta.toFixed(3)}
    </span>
  );
}

interface EfficacyCardProps {
  record: EfficacyRecord;
}

function EfficacyCard({ record }: EfficacyCardProps) {
  const navigate = useNavigate();
  const { uom: cardUom } = useOrgSettings();
  const [expanded, setExpanded] = useState(false);
  const { mission: m } = record;

  return (
    <Card>
      <div className="flex items-start gap-4 p-6 pb-4">
        {/* Efficacy icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
          record.efficacyScore === null ? 'bg-gray-100 dark:bg-gray-700' :
          record.efficacyScore >= 70   ? 'bg-emerald-100 dark:bg-emerald-900/30' :
          record.efficacyScore >= 40   ? 'bg-amber-100 dark:bg-amber-900/30' :
          'bg-red-100 dark:bg-red-900/30'
        }`}>
          {record.efficacyScore === null
            ? <Clock className="w-5 h-5 text-gray-400" />
            : record.efficacyScore >= 70
              ? <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              : record.efficacyScore >= 40
                ? <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                : <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-base font-bold text-gray-900 dark:text-white truncate">{m.name}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs text-gray-500 dark:text-gray-400">{record.boundaryName}</span>
                {m.metadata?.remediation_type && (
                  <span className="text-xs text-gray-400 capitalize">{m.metadata.remediation_type.replace(/_/g, ' ')}</span>
                )}
                {m.metadata?.target_zone_ha != null && (
                  <span className="text-xs text-gray-400">{m.metadata.target_zone_ha} {cardUom.area_unit_label}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${RESUR_STYLE[record.reSurveyStatus]}`}>
                {RESUR_LABEL[record.reSurveyStatus]}
              </span>
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1"
              >
                {expanded ? <><ChevronUp className="w-4 h-4" />Less</> : <><ChevronDown className="w-4 h-4" />Details</>}
              </button>
            </div>
          </div>

          {/* Efficacy bar */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2">
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Pre NDVI</p>
              <p className="text-sm font-mono font-bold text-gray-700 dark:text-gray-300">
                {record.preTreatmentNdvi !== null ? record.preTreatmentNdvi.toFixed(3) : '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Post NDVI</p>
              <p className="text-sm font-mono font-bold text-gray-700 dark:text-gray-300">
                {record.postTreatmentNdvi !== null ? record.postTreatmentNdvi.toFixed(3) : '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Recovery Δ</p>
              <NdviDelta pre={record.preTreatmentNdvi} post={record.postTreatmentNdvi} />
            </div>
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Efficacy Score</p>
              <EfficacyBar score={record.efficacyScore} />
            </div>
          </div>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-6 pb-5 pt-2 border-t border-gray-100 dark:border-gray-700 mt-2 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {record.reSurveyDue && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Re-survey Due</p>
                <p className="font-semibold text-gray-800 dark:text-white">
                  {new Date(record.reSurveyDue).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            )}
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Treatment Date</p>
              <p className="font-semibold text-gray-800 dark:text-white">
                {m.metadata?.treatment_date
                  ? new Date(m.metadata.treatment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : new Date(m.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Mission Status</p>
              <p className="font-semibold text-gray-800 dark:text-white capitalize">{m.status.replace(/_/g, ' ')}</p>
            </div>
          </div>

          {/* Interpretation */}
          {record.efficacyScore !== null && (
            <div className={`rounded-lg p-3 text-sm ${
              record.efficacyScore >= 70
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300'
                : record.efficacyScore >= 40
                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
            }`}>
              {record.efficacyScore >= 70
                ? `Strong recovery detected (${record.efficacyScore}%). NDVI has returned toward baseline — treatment was effective. Consider closing the anomaly loop.`
                : record.efficacyScore >= 40
                  ? `Partial recovery (${record.efficacyScore}%). NDVI is improving but below full baseline. Consider a follow-up inspection or secondary treatment.`
                  : `Low recovery (${record.efficacyScore}%). Little improvement detected. Recommend escalating to ground team for further assessment.`}
            </div>
          )}

          {m.metadata?.survey_mission_id && (
            <button
              type="button"
              onClick={() => navigate(`/missions/${m.metadata!.survey_mission_id}`)}
              className="flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-400 underline hover:no-underline"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View originating survey mission
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const TreatmentEfficacyPage: React.FC = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState<EfficacyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'complete' | 'pending'>('all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch all completed or in_progress remediation missions
      const missionsRes = await api.get('/missions', { params: { limit: 100 } });
      const allMissions: RemediationMission[] = (missionsRes.data.data?.missions ?? missionsRes.data.data ?? []);
      const remMissions = allMissions.filter(
        m => m.mission_type === 'remediation' && (m.status === 'completed' || m.status === 'in_progress' || m.status === 'planned'),
      );

      if (remMissions.length === 0) {
        setRecords([]);
        return;
      }

      // 2. Fetch all satellite boundaries for name lookup
      const boundariesRes = await satelliteBoundariesApi.getAll();
      const boundaries: { id: string; name: string }[] = boundariesRes.data?.boundaries ?? [];
      const boundaryMap = new Map(boundaries.map(b => [b.id, b.name]));

      // 3. For each mission resolve pre/post NDVI from satellite imagery
      const processed = await Promise.all(
        remMissions.map(async (m): Promise<EfficacyRecord> => {
          const boundaryId = m.metadata?.boundary_id ?? '';
          const boundaryName = boundaryMap.get(boundaryId) ?? m.metadata?.field_name ?? 'Unknown field';
          const reSurveyDue = deriveReSurveyDue(m);

          let preTreatmentNdvi: number | null = null;
          let postTreatmentNdvi: number | null = null;

          if (boundaryId) {
            try {
              const imgRes = await satelliteImageryApi.getImagery(boundaryId);
              const imagery: ImageryRecord[] = imgRes.imagery ?? [];
              if (imagery.length > 0) {
                const treatmentDate = m.metadata?.treatment_date ?? m.updated_at;
                // Pre = mean NDVI in the 20-day window before treatment
                const preDateStr = (() => {
                  const d = new Date(treatmentDate);
                  d.setDate(d.getDate() - 7);
                  return d.toISOString().slice(0, 10);
                })();
                preTreatmentNdvi = getNdviMean(imagery, preDateStr, 20);
                // Post = mean NDVI in the 20-day window after expected re-survey date
                if (reSurveyDue) {
                  postTreatmentNdvi = getNdviMean(imagery, reSurveyDue, 20);
                }
              }
            } catch {
              // Non-fatal — imagery unavailable for this field
            }
          }

          const efficacyScore = calcEfficacyScore(preTreatmentNdvi, postTreatmentNdvi);
          const reSurveyStatus = deriveReSurveyStatus(reSurveyDue, postTreatmentNdvi);

          return {
            mission: m,
            preTreatmentNdvi,
            postTreatmentNdvi,
            recoveryDelta: preTreatmentNdvi !== null && postTreatmentNdvi !== null
              ? postTreatmentNdvi - preTreatmentNdvi
              : null,
            efficacyScore,
            reSurveyStatus,
            reSurveyDue,
            boundaryName,
          };
        }),
      );

      setRecords(processed);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load efficacy data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const complete   = records.filter(r => r.reSurveyStatus === 'complete');
  const overdue    = records.filter(r => r.reSurveyStatus === 'overdue');
  const avgEfficacy = complete.length
    ? Math.round(complete.reduce((s, r) => s + (r.efficacyScore ?? 0), 0) / complete.length)
    : null;

  const filtered = filter === 'complete'
    ? records.filter(r => r.reSurveyStatus === 'complete')
    : filter === 'pending'
      ? records.filter(r => r.reSurveyStatus !== 'complete')
      : records;

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-emerald-600" /> Treatment Efficacy
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 8.6 — Pre vs. post-treatment NDVI recovery scores for every completed remediation mission
          </p>
        </div>
        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Stats ── */}
      {!loading && records.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Missions',    value: records.length,           colour: 'text-emerald-600' },
            { label: 'Re-surveys Done',   value: complete.length,          colour: 'text-green-600' },
            { label: 'Overdue',           value: overdue.length,           colour: overdue.length > 0 ? 'text-red-600' : 'text-gray-600' },
            { label: 'Avg Efficacy',      value: avgEfficacy !== null ? `${avgEfficacy}%` : '—', colour: avgEfficacy !== null && avgEfficacy >= 70 ? 'text-emerald-600' : 'text-amber-600' },
          ].map(({ label, value, colour }) => (
            <Card key={label} className="p-4 text-center">
              <p className={`text-2xl font-bold ${colour}`}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── How it works ── */}
      {!loading && records.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Repeat2,    label: 'Auto Re-Survey',  desc: '14-day verification flight triggered automatically after a completed remediation mission' },
            { icon: BarChart2,  label: 'NDVI Recovery',   desc: 'Pre vs. post-treatment satellite NDVI comparison for the treated zone polygon' },
            { icon: TrendingUp, label: 'Efficacy Score',  desc: 'AI-derived 0–100 % recovery score benchmarked against seasonal baseline' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 flex gap-3">
              <Icon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Error ── */}
      {error && <Alert type="error">{error}</Alert>}

      {/* ── Filter tabs ── */}
      {!loading && records.length > 0 && (
        <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
          {([
            ['all',      'All Missions'],
            ['complete', 'Re-survey Complete'],
            ['pending',  'Awaiting Re-survey'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                filter === key
                  ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : records.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <Activity className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No remediation missions to evaluate yet</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">
              Efficacy scores appear once remediation missions reach "planned" or "in progress" status.
            </p>
            <Button variant="outline" size="sm" onClick={() => navigate('/agrotech/remediation')}>
              Go to Remediation Missions
            </Button>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">No missions match the current filter.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map(r => (
            <EfficacyCard key={r.mission.id} record={r} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TreatmentEfficacyPage;
// Made with Bob
